import {
  AccountResource,
  BackendErrorList,
  CalculatedCostStateLoaded,
  CalculatedCostWithTradeType,
  cleanObject,
  CreateOrderResponse,
  formatDateToRequestDay,
  isDefined,
  ListedInstrument,
  ModifyOrderRequest,
  Order,
  OrderConfirmationRequest,
  OrderCostCalculateRequest,
  OrderCreateRequest,
  OrderManagementResource,
  OrderModifyRequest,
  OrderResponseType,
  ordersAtom,
  ResponseMessageType,
  TradeType,
} from '@eih-netbroker/common';
import { isAxiosError } from 'axios';
import BigNumber from 'bignumber.js';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useOpenOrders } from '../../../../shared/services/useOpenOrders';
import { useResource, useResourceHeaders } from '../../../../shared/services/useResource';
import { OrderFormData } from '../../components/order/OrderForm';

export const useOrderManagement = () => {
  const { loadOrders, cancelOrder } = useOpenOrders();
  const [orders] = useAtom(ordersAtom);

  const orderManagementResource = useResource(OrderManagementResource);
  const accountResource = useResource(AccountResource);
  const { getResourceHeaderWithToken, getResourceHeaderForMultiAccount } = useResourceHeaders();

  const generateConfirmationCode = useCallback(
    async (tempOrderId: string, transactionId?: string) => {
      await orderManagementResource.generateConfirmationCode(
        { generateConfirmationCodeRequest: { tempOrderId } },
        getResourceHeaderWithToken(transactionId),
      );
    },
    [getResourceHeaderWithToken, orderManagementResource],
  );

  const createOrder = useCallback(
    async (
      order: OrderCreateRequest,
      accountId?: string,
      transactionId?: string,
      signal?: AbortSignal,
    ): Promise<CreateOrderResponse> => {
      try {
        return (
          await orderManagementResource.createOrder(
            order,
            accountId
              ? { ...getResourceHeaderWithToken(transactionId), accountId }
              : getResourceHeaderWithToken(transactionId),
            signal,
          )
        ).data as CreateOrderResponse;
      } catch (error) {
        if (isAxiosError(error) && (error.response?.data as BackendErrorList)?.errors) {
          return {
            $type: OrderResponseType.Warning,
            messages: (error.response?.data as BackendErrorList).errors.map(e => ({
              msg: e.errorMessage,
              msgType: ResponseMessageType.Error,
            })),
          };
        } else {
          throw error;
        }
      }
    },
    [orderManagementResource, getResourceHeaderWithToken],
  );

  const modifyOrder = useCallback(
    async (
      modifyOrderRequest: ModifyOrderRequest,
      accountId?: string,
      transactionId?: string,
    ): Promise<CreateOrderResponse> => {
      try {
        const response = await orderManagementResource.modifyOrder(
          {
            ...modifyOrderRequest,
          },
          accountId
            ? { ...getResourceHeaderWithToken(transactionId), accountId }
            : getResourceHeaderWithToken(transactionId),
        );

        return response.data as CreateOrderResponse;
      } catch (error) {
        if (isAxiosError(error) && (error.response?.data as BackendErrorList)?.errors) {
          return {
            $type: OrderResponseType.Warning,
            messages: (error.response?.data as BackendErrorList).errors.map(e => ({
              msg: e.errorMessage,
              msgType: ResponseMessageType.Error,
            })),
          };
        } else {
          throw error;
        }
      }
    },
    [orderManagementResource, getResourceHeaderWithToken],
  );

  const costCalculation = useCallback(
    async (order: OrderCostCalculateRequest, transactionId?: string, accountId?: string) => {
      const headers = getResourceHeaderWithToken(transactionId);
      const effectiveHeaders = accountId ? { ...headers, accountId } : headers;
      const response = await orderManagementResource.costCalculation(order, effectiveHeaders);
      return {
        tradeType: order.direction,
        ...response.data,
      } as CalculatedCostWithTradeType;
    },
    [orderManagementResource, getResourceHeaderWithToken],
  );

  const exportPdfFile = useCallback(
    async (order: OrderCreateRequest, transactionId?: string) => {
      return await orderManagementResource.exportPcsFile(order, getResourceHeaderWithToken(transactionId));
    },
    [orderManagementResource, getResourceHeaderWithToken],
  );

  const mapOrderFormToOrderRequest = useCallback((order: OrderFormData): OrderCreateRequest => {
    const exchangeParams =
      order.currencyExchangeParams &&
      order.currencyExchangeParams !== 'initialized' &&
      order.currencyExchangeParams.sourceAmount
        ? {
            hasCurrencyExchange: true,
            currencySourceAmount: order.currencyExchangeParams.sourceAmount,
            currencyTargetAmount: order.currencyExchangeParams.targetAmount,
            exchangeRate: order.currencyExchangeParams.exchangeRate,
            currencySourceCurrency: order.currencyExchangeParams.sourceCurrency,
          }
        : { hasCurrencyExchange: false };
    return {
      isin: order.instrument!.isin,
      quantity: order.quantity!,
      stopActivationPrice: order.stopActivationPrice,
      expiryType: order.expiryType!,
      expiry: isDefined(order.expiry) ? formatDateToRequestDay(order.expiry) : undefined,
      direction: order.tradeType!,
      orderType: order.orderType!,
      tradingSession: order.tradingSession!,
      mic: order.instrument!.mic,
      price: order.price!,
      inventoryManagementMethod: order.inventoryManagementMethod,
      checkAutoConfirm: false,
      ...exchangeParams,
    };
  }, []);

  const mapOrderFormToOrderModifyRequest = useCallback((order: OrderFormData): OrderModifyRequest => {
    const exchangeParams =
      order.currencyExchangeParams &&
      order.currencyExchangeParams !== 'initialized' &&
      order.currencyExchangeParams.sourceAmount
        ? {
            hasCurrencyExchange: true,
            currencySourceAmount: order.currencyExchangeParams.sourceAmount,
            currencyTargetAmount: order.currencyExchangeParams.targetAmount,
            exchangeRate: order.currencyExchangeParams.exchangeRate,
            currencySourceCurrency: order.currencyExchangeParams.sourceCurrency,
          }
        : { hasCurrencyExchange: false };

    return {
      quantity: order.quantity!,
      stopActivationPrice: order.stopActivationPrice,
      expiryType: order.expiryType!,
      expiry: isDefined(order.expiry) ? formatDateToRequestDay(order.expiry) : undefined,
      orderType: order.orderType!,
      tradingSession: order.tradingSession!,
      price: order.price!,
      inventoryManagementMethod: order.inventoryManagementMethod,
      ...exchangeParams,
    };
  }, []);

  const mapOrderToOrderForm = useCallback(
    (order: Order): OrderFormData =>
      cleanObject({
        instrument: order.instrument,
        orderType: order.orderType,
        tradeType: order.direction,
        quantity: order.quantity,
        stopActivationPrice: order.stopActivationPrice,
        price: order.price,
        expiryType: order.expiryType,
        expiry: order.expiry ? new Date(order.expiry) : undefined,
        inventoryManagementMethod: order.inventoryManagementMethod,
        tradingSession: order.tradingSession,
      }),
    [],
  );

  const cancelAllOrders = useCallback(async () => {
    await orderManagementResource.cancelAllOrders(getResourceHeaderForMultiAccount());
    loadOrders(false);
  }, [orderManagementResource, loadOrders, getResourceHeaderForMultiAccount]);

  const orderConfirmation = useCallback(
    (request: OrderConfirmationRequest, transcationId?: string) => {
      return orderManagementResource.orderConfirmation(request, getResourceHeaderWithToken(transcationId));
    },
    [getResourceHeaderWithToken, orderManagementResource],
  );

  const getTotalPrices = useCallback(
    (
      calculatedCost: CalculatedCostWithTradeType,
      price: number,
    ): Pick<CalculatedCostStateLoaded, 'totalPriceBuy' | 'totalPriceSell'> => {
      if (calculatedCost.tradeType === TradeType.Buy) {
        const totalPrice = new BigNumber(price).plus(calculatedCost.buyAmount).toNumber();
        return {
          totalPriceBuy: totalPrice,
          totalPriceSell: totalPrice,
        };
      } else if (calculatedCost.tradeType === TradeType.Sell) {
        const totalPrice = new BigNumber(price).plus(calculatedCost.sellAmount).toNumber();
        return {
          totalPriceBuy: totalPrice,
          totalPriceSell: totalPrice,
        };
      } else {
        const totalPriceBuy = new BigNumber(price).plus(calculatedCost.buyAmount).toNumber();
        const totalPriceSell = new BigNumber(price).plus(calculatedCost.sellAmount).toNumber();
        return {
          totalPriceBuy: totalPriceBuy,
          totalPriceSell: totalPriceSell,
        };
      }
    },
    [],
  );

  const getMaxOpenPosition = useCallback(
    async (instrument: ListedInstrument) => {
      return orderManagementResource.getMaxOpenPosition(
        {
          isin: instrument.isin,
          mic: instrument.mic,
        },
        getResourceHeaderWithToken(),
      );
    },
    [orderManagementResource, getResourceHeaderWithToken],
  );

  const loadBalanceForOrder = useCallback(
    async (sourceCurrency: string, mic: string) => {
      return (
        await accountResource.getCurrencyBalanceForOrder(
          {
            sourceCcyCode: sourceCurrency,
            mic,
          },
          getResourceHeaderWithToken(),
        )
      ).data;
    },
    [accountResource, getResourceHeaderWithToken],
  );

  return useMemo(
    () => ({
      createOrder,
      costCalculation,
      exportPdfFile,
      mapOrderFormToOrderRequest,
      mapOrderFormToOrderModifyRequest,
      mapOrderToOrderForm,
      cancelAllOrders,
      cancelOrder,
      modifyOrder,
      orderConfirmation,
      getTotalPrices,
      orders,
      getMaxOpenPosition,
      generateConfirmationCode,
      loadBalanceForOrder,
    }),
    [
      createOrder,
      costCalculation,
      exportPdfFile,
      mapOrderFormToOrderRequest,
      mapOrderFormToOrderModifyRequest,
      mapOrderToOrderForm,
      cancelAllOrders,
      cancelOrder,
      modifyOrder,
      orderConfirmation,
      getTotalPrices,
      orders,
      getMaxOpenPosition,
      generateConfirmationCode,
      loadBalanceForOrder,
    ],
  );
};
